<script setup lang="ts">
import { PanelLeft } from 'lucide-vue-next'
import IconLogoWordmark from '@/components/icons/IconLogoWordmark.vue'

interface Props {
  isOpen: boolean
}

defineProps<Props>()

const emit = defineEmits<{
  toggle: []
}>()

defineOptions({
  name: 'SidebarLeftHeader',
})

function handleToggle() {
  emit('toggle')
}
</script>

<template>
  <div class="sidebar-header">
    <div class="header-content" :data-open="isOpen">
      <!-- When collapsed: Show toggle button with IconLogo/PanelLeft on hover -->
      <button
        v-if="!isOpen"
        type="button"
        class="logo-toggle-button group"
        aria-label="Expand Sidebar"
        @click.stop="handleToggle"
      >
        <IconLogo class="logo-icon group-hover:hidden" />
        <PanelLeft :size="24" class="panel-icon hidden group-hover:block" />
        <span class="sr-only">Expand Sidebar</span>
      </button>

      <!-- When expanded: Show logo link and collapse button -->
      <template v-else>
        <a
          href="/"
          class="logo-link"
          aria-label="Go to home page"
          @click.stop
        >
          <IconLogo class="logo-icon" />

          <!-- Wordmark (shown when expanded) -->
          <span class="logo-wordmark">
            <IconLogoWordmark class="wordmark-svg" aria-label="Snack Prompt" />
          </span>
        </a>

        <!-- Toggle button (shown when expanded) -->
        <button
          type="button"
          class="toggle-button"
          :data-open="isOpen"
          :aria-label="isOpen ? 'Collapse Sidebar' : 'Expand Sidebar'"
          @click.stop="handleToggle"
        >
          <PanelLeft :size="16" />
          <span class="sr-only">Toggle Sidebar</span>
        </button>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sidebar-header {
  @apply
    flex
    flex-col
    shrink-0
    gap-6
    p-3;
}

.header-content {
  @apply
    flex
    items-center
    h-12;
}

// Logo toggle button (when collapsed)
.logo-toggle-button {
  @apply
    flex
    items-center
    justify-center
    rounded-full
    outline-none
    cursor-pointer
    transition-colors
    duration-200
    hover:bg-gray-100
    w-12
    h-12
    p-3;
}

.logo-link {
  @apply
    flex
    items-center
    hover:opacity-80
    transition-opacity
    cursor-pointer
    ml-1
    p-2;
}

.logo-icon {
  @apply
    shrink-0
    w-8
    h-8;
}

.panel-icon {
  @apply shrink-0 text-muted-foreground;
}

// Wordmark (text logo)
.logo-wordmark {
  @apply
    flex
    items-center
    gap-2
    overflow-hidden
    ml-6;
}

.wordmark-svg {
  @apply
    h-7
    min-w-[137px]
    w-[137px];
}

// Toggle button
.toggle-button {
  @apply
    inline-flex
    items-center
    justify-center
    gap-2
    whitespace-nowrap
    rounded-full
    text-sm
    font-medium
    shrink-0
    outline-none
    cursor-pointer
    transition-all
    duration-300
    overflow-hidden
    hover:bg-gray-100
    focus-visible:ring-2
    focus-visible:ring-gray-300
    focus-visible:ring-offset-2
    w-0
    h-7
    ml-auto;

  &[data-open='true'] {
    @apply w-7;
  }

  &[data-open='false'] {
    @apply opacity-0 pointer-events-none;
  }
}

// Screen reader only
.sr-only {
  @apply
    absolute
    w-px
    h-px
    p-0
    -m-px
    overflow-hidden
    whitespace-nowrap
    border-0;

  clip: rect(0, 0, 0, 0);
}
</style>
